/* @flow */
import { dispatcher } from 'lib/Dispatcher';
import API from 'lib/RemindAPI';
import classesDefaultsFragment from 'groups/fragments/classesDefaultsFragment.gql';
import Group, { GROUP_MEMBER_PAGE_SIZE } from 'entities/Group';
import Groups from 'collections/Groups';
import GroupMemberships from 'collections/GroupMemberships';
import OrganizationActions from 'actions/OrganizationActions';
import RelationshipActions from 'actions/RelationshipActions';
import RootGroupMemberships from 'collections/RootGroupMemberships';
import { rootGroup } from 'entities/RootGroup';
import { ActionTypes as GroupActionTypes } from 'groups/constants/GroupConstants';
import { ActionTypes as GroupMembershipActionTypes } from 'constants/GroupMembershipConstants';
import ToastActions from 'actions/ToastActions';
import gql from 'graphql-tag';

import type { LoadGroupMembershipsParams } from 'entities/Group';

const GroupActions = {
  // TODO kill this action that's not an action (doesn't even dispatch)
  searchGroupByCode(groupCode: string): Promise<?Group> {
    return Group.fetchByCode(groupCode);
  },

  async loadGroups(classUuids?: string[]): Promise<Groups> {
    const allMine = classUuids === undefined;

    const query = allMine ?
      gql`
        ${classesDefaultsFragment}
        query {
          me {
            id
            ownedClasses: classes(classType: "owned") {
              ...classesDefaults
            }
            subscribedClasses: classes(classType: "subscribed") {
              ...classesDefaults
            }
            organizationClasses: classes(classType: "organization") {
              ...classesDefaults
            }
            districtClasses: classes(classType: "district") {
              ...classesDefaults
            }
          }
        }` :
      gql`
        ${classesDefaultsFragment}
        query ($classUuids: [String]!) {
          classes(uuids: $classUuids) {
            ...classesDefaults
          }
        }`;

    const data = await API.execute(query, { classUuids });

    const classes = allMine ?
      data.me.ownedClasses.concat(data.me.subscribedClasses, data.me.organizationClasses, data.me.districtClasses) :
      data.classes;

    const groups = classes
      .filter(Boolean) // GraphQL returns null for unavailable values
      .map(group => Group.fromJSON(group));

    dispatcher.dispatch({
      type: GroupActionTypes.LIST_GROUPS,
      groups,
      allMine,
    });

    return groups;
  },

  resetStore(): void {
    dispatcher.dispatch({
      type: GroupActionTypes.RESET_STORE,
    });
  },

  loadPotentialOwners(group: Group, page: number = 1, perPage: number = 20) {
    return group.loadPotentialOwners(page, perPage).then(potentialOwners => {
      /* todo dispatch event, unnecessary for now */
      return potentialOwners;
    });
  },

  async loadRootGroupMemberships(params: LoadGroupMembershipsParams = {}): Promise<GroupMemberships> {
    const data = {
      page: 1,
      perPage: GROUP_MEMBER_PAGE_SIZE,
      ...params,
    };

    const members = await RootGroupMemberships.fetch({ data });

    const groupMembers = members.getGroupMemberships();

    dispatcher.dispatch({
      type: GroupMembershipActionTypes.LOAD_CLASS_MEMBERS,
      members: groupMembers,
      group: rootGroup,
      membersRequested: data.perPage,
      query: data.query,
    });

    return groupMembers;
  },

  async loadGroupMemberships(group: Group, params: LoadGroupMembershipsParams = {}): Promise<GroupMemberships> {
    if (group.isRoot()) {
      return this.loadRootGroupMemberships(params);
    }

    const members = await group.loadGroupMemberships(params);

    dispatcher.dispatch({
      type: GroupMembershipActionTypes.LOAD_CLASS_MEMBERS,
      membersRequested: params.perPage || GROUP_MEMBER_PAGE_SIZE,
      query: params.query || '',
      members,
      group,
    });

    return members;
  },

  async loadArchivedGroups(): Promise<void> {
    const groups = await Groups.fetchArchived();

    dispatcher.dispatch({
      type: GroupActionTypes.LIST_ARCHIVED_GROUPS,
      groups,
    });
  },

  // $FlowFixMe
  addSubscribers(group: Group, subscribers) {
    return group.addSubscribers(subscribers).then(members => {
      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      const relationshipUpdates = members.map(member =>
        RelationshipActions.updateRelationship(member.getUserId()));
      return Promise.all(relationshipUpdates);
    });
  },

  async promoteGroupMemberships(group: Group, memberIds: number[]) {
    const members = await group.promoteGroupMemberships(memberIds);
    dispatcher.dispatch({
      type: GroupMembershipActionTypes.PROMOTED_CLASS_MEMBERS,
      members,
      group,
    });
    return members;
  },

  async resignOwnership(group: Group) {
    await group.resignOwnership();

    dispatcher.dispatch({
      type: GroupActionTypes.RESIGN_OWNERSHIP,
      group,
    });
  },

  // $FlowFixMe
  removeMember(group: Group, subscriber) {
    return group.removeMember(subscriber).then(() => {
      dispatcher.dispatch({
        type: GroupMembershipActionTypes.REMOVE_SUBSCRIBER,
        subscriber,
      });

      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      return RelationshipActions.updateRelationship(subscriber.id);
    });
  },

  async create(groupToCreate: Group): Promise<Group> {
    const group = await groupToCreate.create();

    dispatcher.dispatch({
      type: GroupActionTypes.CREATE_GROUP,
      groups: [group],
    });

    return group;
  },

  patch(groupToPatch: Group, attributes: Object, toastText: ?string, showToast: boolean = true) {
    return groupToPatch.patch(attributes).then((group) => {
      if (attributes.organization_id !== undefined) {
        dispatcher.dispatch({
          type: GroupActionTypes.CHANGE_ORG,
          groupUuid: group.getUuid(),
        });
      }

      if (showToast) {
        ToastActions.showSuccessToast(toastText || 'Saved');
      }

      dispatcher.dispatch({
        type: GroupActionTypes.UPDATE_GROUP,
        group,
      });

      return group;
    });
  },

  // $FlowFixMe
  setGroupsAllowedToStartChat(groupsToAllow) {
    return Groups.setGroupsAllowedToStartChat(groupsToAllow).then((groups) => {
      dispatcher.dispatch({
        type: GroupActionTypes.GROUPS_UPDATED,
        groups,
      });
    });
  },

  // $FlowFixMe
  sendAgainGroupMessage(groupId, oldMessage) {
    dispatcher.dispatch({
      type: GroupActionTypes.SEND_AGAIN,
      groupId,
      message: oldMessage.reuse(),
    });
  },

  subscribeToGroup(groupToSubscribe: Group): Promise<Group> {
    return groupToSubscribe.subscribe()
      .then(group => {
        const org = group.getOrganization();
        if (org) {
          OrganizationActions.getOrLoadOrganization(org.id);
        }

        // TODO: This is temporary. This is needed to ensure visible views get the correct state
        // since subscribing to a group no longer gets a Subscription object.
        const relationshipUpdates = group.getOwners()
          .map(member => RelationshipActions.updateRelationship(member.getUserId()));
        return Promise.all(relationshipUpdates).then(() => {
          dispatcher.dispatch({
            type: GroupActionTypes.SUBSCRIBED,
            group,
          });
          return group;
        });
      },
      e => {
        if (e.code === 'duplicate_membership') {
          console.warn(e);
        } else {
          throw e;
        }
      });
  },

  subscribeToGroupWithCode(code: string): Promise<Group> {
    return this.subscribeToGroup(Group.createGroupFromCode(code));
  },

  unsubscribeFromGroup(group: Group) {
    return group.unsubscribe().then(() => {
      // TODO: This is temporary. This is needed to ensure visible views get the correct state since
      // subscribing to a group no longer gets a Subscription object.
      const relationshipUpdates = group.getOwners()
        .map(member => RelationshipActions.updateRelationship(member.getUserId()));
      return Promise.all(relationshipUpdates).then(() => {
        dispatcher.dispatch({
          type: GroupActionTypes.UNSUBSCRIBED,
          group,
        });
        return group;
      });
    });
  },

  removeGroup(group: Group) {
    dispatcher.dispatch({
      type: GroupActionTypes.DELETE_GROUP,
      group,
    });
  },

  async archiveGroup(groupToArchive: Group): Promise<Group> {
    const group = await groupToArchive.archive();

    dispatcher.dispatch({
      type: GroupActionTypes.ARCHIVE,
      group,
    });

    return group;
  },

  async reuseGroup(archivedGroup: Group, organizationId: ?number, classCode?: string): Promise<Group> {
    const group = await archivedGroup.reuse(organizationId, classCode);

    dispatcher.dispatch({
      type: GroupActionTypes.REUSE,
      group,
      archivedGroup,
    });

    return group;
  },

  async archiveAndReuseGroup(groupToArchive: Group, organizationId: ?number, classCode?: string): Promise<Group> {
    const archivedGroup = await groupToArchive.archive();
    const group = await archivedGroup.reuse(organizationId, classCode);

    dispatcher.dispatch({
      type: GroupActionTypes.ARCHIVE_AND_REUSE,
      group,
      archivedGroup,
    });

    return group;
  },

  restoreGroup(groupToRestore: Group, organizationId: ?number, classCode?: string): Promise<Group> {
    const group = groupToRestore.unarchive(organizationId, classCode);

    dispatcher.dispatch({
      type: GroupActionTypes.RESTORE,
      group,
    });

    return group;
  },

  enableAllGroupMessageHistory() {
    API.request('/classes', 'PATCH', {
      all: {
        message_history_enabled: true,
      },
    });
  },
};

export default GroupActions;



// WEBPACK FOOTER //
// ./src/groups/actions/GroupActions.js